import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AnalysisSidebar, NavSectionType } from '@popety-io/ui'

const getNavigationSections = (t: any): NavSectionType[] => [
  {
    title: t('estimate.sidebar.overview'),
    color: 'steel',
    items: [
      {
        id: 'summary',
        label: t('estimate.sidebar.summary'),
        icon: 'bar-chart',
      },
    ],
  },
  {
    title: t('estimate.sections.property'),
    color: 'steel',
    items: [
      {
        id: 'property_details',
        label: t('estimate.navigation.property_details'),
        icon: 'building',
      },
      { id: 'history', label: t('estimate.navigation.history'), icon: 'clock' },
      { id: 'land', label: t('estimate.sidebar.land'), icon: 'land-plot' },
      { id: 'building', label: t('estimate.sidebar.building'), icon: 'cube' },
      {
        id: 'energy',
        label: t('estimate.sidebar.energy'),
        icon: 'battery-charging',
      },
      {
        id: 'notes',
        label: t('common.sidebar.items.notes'),
        icon: 'edit',
      },
      {
        id: 'files',
        label: t('common.sidebar.items.files'),
        icon: 'file',
      },
    ],
  },
  {
    title: t('estimate.title'),
    color: 'bronze',
    items: [
      {
        id: 'auto_valuations',
        label: t('estimate.sidebar.autoValuations'),
        icon: 'sparkles',
      },
      {
        id: 'manual_valuations',
        label: t('estimate.sidebar.manualValuations'),
        icon: 'edit',
      },
      {
        id: 'listings_comparables',
        label: t('estimate.navigation.listings_comparables'),
        icon: 'tag',
      },
      {
        id: 'transactions',
        label: t('estimate.navigation.transactions_comparables'),
        icon: 'real-estate-transaction',
      },
    ],
  },
  {
    title: t('estimate.sections.market'),
    color: 'forest',
    items: [
      {
        id: 'purchase_market',
        label: t('estimate.navigation.purchase_market'),
        icon: 'cash',
      },
      {
        id: 'rental_market',
        label: t('estimate.navigation.rental_market'),
        icon: 'home',
      },
    ],
  },
  {
    title: t('estimate.sections.environment'),
    color: 'deepBlue',
    items: [
      {
        id: 'building_permits',
        label: t('estimate.sidebar.buildingPermits'),
        icon: 'building-permit',
      },
      {
        id: 'transport_access',
        label: t('estimate.sidebar.transportAccess'),
        icon: 'car',
      },
      {
        id: 'amenities',
        label: t('estimate.navigation.amenities'),
        icon: 'store',
      },
      {
        id: 'noise_analysis',
        label: t('estimate.navigation.noise_analysis'),
        icon: 'activity',
      },
      {
        id: 'sunlight',
        label: t('estimate.navigation.sunlight_study'),
        icon: 'sun',
      },
    ],
  },
  {
    title: t('estimate.sections.social_economic'),
    color: 'wine',
    items: [
      {
        id: 'demographics',
        label: t('estimate.navigation.demographics'),
        icon: 'users',
      },
      {
        id: 'employment',
        label: t('estimate.sidebar.employment'),
        icon: 'briefcase',
      },
      {
        id: 'taxes',
        label: t('estimate.sidebar.taxes'),
        icon: 'receipt',
      },
      {
        id: 'electricity',
        label: t('estimate.sidebar.electricity'),
        icon: 'zap',
      },
    ],
  },
]

/**
 * Filter navigation sections based on available sections
 * If availableSections is undefined, all sections are shown (backward compatibility)
 */
const filterSections = (
  sections: NavSectionType[],
  availableSections?: string[]
): NavSectionType[] => {
  // If no filter specified, show all sections. Notes and files are opt-in, so
  // a legacy share that lists nothing must not grow two empty tabs.
  if (!availableSections || availableSections.length === 0) {
    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) => item.id !== 'notes' && item.id !== 'files'
        ),
      }))
      .filter((section) => section.items.length > 0)
  }

  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) =>
        availableSections.includes(item.id)
      ),
    }))
    .filter((section) => section.items.length > 0)
}

export interface PublicEstimateSidebarProps {
  activeSection: string
  onSectionChange: (sectionId: string) => void
  availableSections?: string[]
}

export const PublicEstimateSidebar: React.FC<PublicEstimateSidebarProps> = ({
  activeSection,
  onSectionChange,
  availableSections,
}) => {
  const { t } = useTranslation()

  const filteredSections = useMemo(() => {
    let sections = getNavigationSections(t)
    sections = filterSections(sections, availableSections)

    return sections
  }, [t, availableSections])

  return (
    <AnalysisSidebar
      sections={filteredSections}
      activeSection={activeSection}
      onSectionChange={onSectionChange}
    />
  )
}
